/**
 * SOUND. Ours - all 52 effects and both themes, every one of them made for this game.
 *
 * 🚨 **WE MADE 52 SOUND EFFECTS AND THIS FILE WAS PLAYING 12.** Alex, 2026-08-14:
 *
 * > *"the music for the menu page (and all pages except the battle) is still not implemented. What
 * > are you doing? ... Stop wasting time eyeballing and guessing and throwing away the over 10
 * > days/hundreds of hours of work that we have already done!"*
 *
 * He is right, and the sound design was the clearest case of it: the clips are **named by the exact
 * game event they belong to** - `TableCard_Attack`, `TableCard_Death`, `YourTurn`,
 * `Timer_RunningOut`, `VictoryPopup`, `Effect_Taunt`, `Spell_Hex`. Each was sourced and reviewed
 * one at a time (`art-src/sfx-review/_INDEX.txt`, CC0, one file per sound, with a beep track so
 * theirs and ours could be compared). Playing a generic click over that is discarding finished work.
 *
 * ⭐ **THE CLIP LIST IS GENERATED, THE MAPPING IS DECLARED, AND BOTH ARE GATED.**
 * `audio-clips.generated.ts` is every clip that exists; `EVENTS` and `EFFECT_SFX` say when each one
 * plays; `tools/verify_audio_coverage.py` fails on any clip neither mapped nor explicitly waived.
 * That is what stops the set silently shrinking back to twelve.
 *
 * 🚨 **AUTOPLAY: THE GESTURE MAY HAVE ALREADY HAPPENED.** A browser will not play anything before
 * the document has been interacted with - that is the rule, not a bug to route around. But arming
 * only for the NEXT gesture is precisely why the menu was silent: the click that navigated there was
 * already spent, and on the menu there may be no second click for minutes.
 * `navigator.userActivation.hasBeenActive` is the browser's own record that this document already
 * qualifies, so it is checked FIRST.
 *
 * ⚠️ **SFX ARE POOLED.** One `Audio` element cannot play twice at once, so two cards landing
 * together would drop a sound. Each effect keeps a small ring of clones.
 *
 * ⚠️ **A MISSING FILE MUST NOT THROW INTO THE GAME LOOP.** A sound that fails to load is logged once
 * and then skipped - losing a click is a blemish, an exception mid-drag is a broken turn.
 */
import { CLIPS, type ClipName } from "../audio-clips.generated";

const SFX_DIR = "/audio/sfx/";
const MUSIC_DIR = "/audio/music/";

/**
 * GAME EVENT -> CLIP. The left column is what happens; the right is the file that says so.
 *
 * ⭐ Read the right-hand column as the sound design document, because that is what it is.
 */
export const EVENTS = {
  // ── the hand and the table ──────────────────────────────────────────────────────────────────
  cardHover: "Card_Hover",
  cardPick: "Card_StartDrag",
  cardDrop: "Card_Release",
  cardSpawn: "TableCard_Spawn",
  cardBurn: "Card_Delete card",
  cardDraw: "Card_take card 2",
  // ⚠️ DRAWN vs GRANTED are different moments and the sound set distinguishes them: a card taken
  // from your deck is `Card_take card 2`; one CONJURED into your hand by an effect ("Add a Cragborn
  // Goat to your hand") is `Card_Spawn`. Collapsing them loses the tell that something was given.
  cardGranted: "Card_Spawn",
  cardInfo: "Card_Card infos",
  shuffle: "Card_mix",

  // ── combat ──────────────────────────────────────────────────────────────────────────────────
  attack: "TableCard_Attack",
  damage: "TableCard_Damage",
  death: "TableCard_Death",
  heal: "TableCard_Heal",
  buff: "TableCard_Buff",

  // ── targeting ───────────────────────────────────────────────────────────────────────────────
  arrowStart: "Arrow_Start",
  arrowSelect: "Arrow_Select",

  // ── the match frame ─────────────────────────────────────────────────────────────────────────
  yourTurn: "YourTurn",
  timerLow: "Timer_RunningOut",
  victory: "VictoryPopup",
  defeat: "LostPopup",

  // ⭐ THE HERO POWER. `_HA` is the original's suffix for a hero ability.
  heroPower: "Spell_Arm_HA",

  // ── outside the match ───────────────────────────────────────────────────────────────────────
  click: "Button_Click",
  queue: "Lobby_QuickMatch",
  opponentFound: "Lobby_OpponentEntered",
} as const satisfies Record<string, ClipName>;

export type SfxName = keyof typeof EVENTS;

/**
 * ENGINE KEYWORD -> CLIP, for effects that announce themselves.
 *
 * ⭐ **KEYED BY `EffectKeyword`, THE NAME THE ENGINE ITSELF USES.** So when the server says an
 * effect was applied, the sound follows with no translation table for anyone to maintain. See
 * `match/rules.ts` for how a runtime effect resolves to its keyword.
 */
export const EFFECT_SFX = {
  DeBuffAttack: "Effect_BuffDown",
  DeBuffHealth: "Effect_BuffDown",
  BuffAttack: "Effect_BuffUp",
  BuffHealth: "Effect_BuffUp",
  BuffArmor: "Effect_BuffUp",
  Curse: "Effect_Curse",
  Stunning: "Effect_Freeze",
  Sleeping: "Effect_Freeze",
  Immune: "Effect_Immune",
  ImmuneToDamage: "Effect_Immune",
  LifeSteal: "Effect_LifeSteal",
  Poisoning: "Effect_Poison",
  Rage: "Effect_Rage",
  Reborning: "Effect_Resurrection",
  Resurrect: "Effect_Resurrection",
  Summon: "Effect_Summon",
  Taunting: "Effect_Taunt",
  Silence: "Spell_silence",
  Destroy: "Spell_DestroyCard_HA",
  Blacksmith: "Spell_Blacksmith",
  Slash: "Spell_Release",
  SwapStats: "Spell_switch",
  ManaGain: "Spell_ManaBurn",
  Heal: "TableCard_Heal",
} as const satisfies Record<string, ClipName>;

/**
 * Clips deliberately NOT wired, each with its reason. The coverage gate reads this.
 *
 * ⛔ **A WAIVER IS A DECISION, NOT A PLACEHOLDER.** "Not used yet" is not a reason: if a clip has no
 * home, either the feature it belongs to is missing - say so, and it becomes a work item - or the
 * clip is redundant, and say that instead. The gate exists because "we will wire it later" is how
 * forty finished sounds sat unused for weeks.
 */
export const WAIVED: Readonly<Record<string, string>> = Object.freeze({
  PoisoningSFX: "duplicate of Effect_Poison, which is the clip the keyword map uses",
  "Hit_DamageAllCards_DamageOpponent_HA": "a hero-ability sound from the ORIGINAL roster; hero powers now work, but these clips do not map 1:1 onto our four powers - needs an effect-id to clip mapping",
  "Hit_DamageRandomCard_HA": "a hero-ability sound from the ORIGINAL roster; hero powers now work, but these clips do not map 1:1 onto our four powers - needs an effect-id to clip mapping",
  "Spell_Disarm_HA": "a hero-ability sound from the ORIGINAL roster; hero powers now work, but these clips do not map 1:1 onto our four powers - needs an effect-id to clip mapping",
  "Spell_EvilEye_HA": "a hero-ability sound from the ORIGINAL roster; hero powers now work, but these clips do not map 1:1 onto our four powers - needs an effect-id to clip mapping",
  "Spell_ExtraLava_ExtraHealth_HA": "a hero-ability sound from the ORIGINAL roster; hero powers now work, but these clips do not map 1:1 onto our four powers - needs an effect-id to clip mapping",
  "Spell_silenceEnemies_HA": "a hero-ability sound from the ORIGINAL roster; hero powers now work, but these clips do not map 1:1 onto our four powers - needs an effect-id to clip mapping",
  "Spell_spellDamage_HA": "a hero-ability sound from the ORIGINAL roster; hero powers now work, but these clips do not map 1:1 onto our four powers - needs an effect-id to clip mapping",
  "Spell_undefeatable_HA": "a hero-ability sound from the ORIGINAL roster; hero powers now work, but these clips do not map 1:1 onto our four powers - needs an effect-id to clip mapping",
  "Spell_Hex": "Hex is an ACTIVE ability; the ability button is not built yet",
  "Spell_Refund": "no Refund effect is wired to any card in the served database",
  "Spell_DestroyCard_HA": "mapped through EFFECT_SFX (Destroy), not through a game event",
  "Spell_Release": "mapped through EFFECT_SFX (Slash), not through a game event",
  "Spell_switch": "mapped through EFFECT_SFX (SwapStats), not through a game event",
  "Spell_ManaBurn": "mapped through EFFECT_SFX (ManaGain), not through a game event",
  "Spell_silence": "mapped through EFFECT_SFX (Silence), not through a game event",
  "Spell_Blacksmith": "mapped through EFFECT_SFX (Blacksmith), not through a game event",
});

const POOL = 3;

/**
 * What a player who has never touched the settings hears.
 *
 * ⭐ **EXPORTED BECAUSE A GATE HAS TO ASK.** `verify_music.py` failed on 2026-08-15 against a
 * working client because the harness ACCOUNT had music saved at 0.09 - a tester's own preference
 * read as "the music is inaudible". The honest question is whether the DEFAULT is audible; a player
 * who turned it down chose that.
 *
 * ⚠️ Music sits below effects deliberately: it is the bed, and a theme competing with the card
 * sounds is the complaint that started this ("start the sound softer, it starts far too loud").
 */
export const DEFAULT_SFX_VOLUME = 0.42;
export const DEFAULT_MUSIC_VOLUME = 0.30;

class Audio_ {
  private pools = new Map<string, HTMLAudioElement[]>();
  private cursor = new Map<string, number>();
  private music: HTMLAudioElement | null = null;
  private armed = false;
  private failed = new Set<string>();

  /**
   * 0..1. Persisted, because a player who muted a game expects it to stay muted.
   *
   * ⭐ **SOFT ONSET, AUDIBLE LEVEL - THEY ARE DIFFERENT COMPLAINTS.** Alex, 2026-08-14, said both
   * *"start the sound softer, it starts far too loud"* AND *"the main menu music is not loading at
   * all"*. Measured: the music WAS loading and playing (`MainTheme.ogg`, ready 4, no error) at
   * volume 0.1 - inaudible. So the fix is not "quieter": it is a long FADE so nothing arrives at
   * full level, with a level you can actually hear once it is there. A first-run volume is not a preference, it is an AMBUSH: the player has
   * not asked for anything yet, and the only wrong answer that cannot be undone is the one that
   * makes them reach for the tab close. Starting low and letting them raise it is the safe
   * direction of the error.
   *
   * ⚠️ These are the FALLBACKS only. A returning player's saved values come from their profile
   * (`ui/settings.ts`) and overwrite these on load.
   */
  private sfxVolume = Number(localStorage.getItem("revival.vol.sfx") ?? String(DEFAULT_SFX_VOLUME));
  private musicVolume = Number(localStorage.getItem("revival.vol.music") ?? String(DEFAULT_MUSIC_VOLUME));

  arm(): void {
    if (this.armed) return;

    // 🚨 See the header: the gesture that got the player here is already spent, so waiting for the
    // NEXT one is why the menu was silent.
    const activation = (navigator as Navigator & { userActivation?: { hasBeenActive?: boolean } })
      .userActivation;
    if (activation?.hasBeenActive) {
      this.armed = true;
      if (this.music) void this.music.play().catch(() => undefined);
      return;
    }

    const go = () => {
      if (this.armed) return;
      this.armed = true;
      for (const t of ["pointerdown", "keydown", "touchstart"]) {
        window.removeEventListener(t, go);
      }
      if (this.music) void this.music.play().catch(() => undefined);
    };
    for (const t of ["pointerdown", "keydown", "touchstart"]) {
      window.addEventListener(t, go, { passive: true });
    }
  }

  private pool(key: string, file: string): HTMLAudioElement[] {
    let p = this.pools.get(key);
    if (!p) {
      p = Array.from({ length: POOL }, () => {
        const a = new Audio(SFX_DIR + file);
        a.preload = "auto";
        a.addEventListener("error", () => {
          if (!this.failed.has(file)) {
            this.failed.add(file);
            // ⚠️ Once, not per play: a missing file would otherwise flood the console every hover.
            console.warn(`[audio] ${file} did not load - that sound is silent for this session`);
          }
        });
        return a;
      });
      this.pools.set(key, p);
      this.cursor.set(key, 0);
    }
    return p;
  }

  private fire(key: string, file: string, volumeScale: number): void {
    // ⚠️ The pool is built even when not armed, so the clip is already decoded by the time the
    // first gesture lands. Arming is checked at PLAY, not at load.
    const p = this.pool(key, file);
    if (!this.armed) return;
    const i = (this.cursor.get(key) ?? 0) % p.length;
    this.cursor.set(key, i + 1);
    const a = p[i];
    if (!a) return;
    a.volume = Math.max(0, Math.min(1, this.sfxVolume * volumeScale));
    a.currentTime = 0;
    void a.play().catch(() => undefined);
  }

  play(name: SfxName, volumeScale = 1): void {
    this.fire(name, CLIPS[EVENTS[name]], volumeScale);
  }

  /**
   * Play the sound for an engine effect keyword, if it has one.
   *
   * ⚠️ Silent for keywords with no clip - that is a mapping decision recorded in `EFFECT_SFX`, not
   * a failure, and it must not warn on every application of a common buff.
   */
  playEffect(keyword: string, volumeScale = 1): void {
    const clip = (EFFECT_SFX as Record<string, ClipName | undefined>)[keyword];
    if (!clip) return;
    this.fire("fx:" + keyword, CLIPS[clip], volumeScale);
  }

  /**
   * Switch the music.
   *
   * ⚠️ **CROSSFADED, NOT CUT.** A hard cut between themes when a match starts is the most noticeable
   * audio flaw a card game can have, because it always lands on a transition the player is watching.
   */
  setMusic(track: "menu" | "battle" | "none"): void {
    const file = track === "menu" ? "MainTheme.ogg" : track === "battle" ? "BattleTheme.ogg" : null;

    if (!file) {
      this.fadeOut(this.music);
      this.music = null;
      return;
    }
    if (this.music && this.music.src.endsWith(file)) return;

    const next = new Audio(MUSIC_DIR + file);
    next.loop = true;
    // ⚠️ **NOT FROM SILENCE.** Fading up from 0 over two seconds fixed "too loud" and immediately
    // produced the opposite report - Alex, 2026-08-14: *"the music loads in with a long delay."*
    // A track that starts inaudible IS late, however soft the curve. It now enters at a third of
    // its level, which is present at once, and rises the rest of the way quickly.
    next.volume = Math.max(0, Math.min(1, this.musicVolume * 0.34));
    next.addEventListener("error", () => {
      console.warn(`[audio] ${file} did not load - the music is silent for this session`);
    });

    const prev = this.music;
    this.music = next;
    if (this.armed) void next.play().catch(() => undefined);

    // ⚠️ **SHORT ENOUGH NOT TO FEEL LATE, LONG ENOUGH NOT TO SLAM.** 2200ms read as a delay; 900ms
    // from silence read as a slam. It starts audible (above) and takes 1100ms to reach level.
    this.fadeTo(next, this.musicVolume, 1100);
    this.fadeOut(prev);
  }

  private fadeTo(a: HTMLAudioElement | null, target: number, ms: number): void {
    if (!a) return;
    const from = a.volume;
    const start = performance.now();
    const step = () => {
      const t = Math.min(1, (performance.now() - start) / ms);
      a.volume = Math.max(0, Math.min(1, from + (target - from) * t));
      if (t < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }

  private fadeOut(a: HTMLAudioElement | null): void {
    if (!a) return;
    this.fadeTo(a, 0, 700);
    window.setTimeout(() => {
      a.pause();
      a.src = "";
    }, 800);
  }

  setVolumes(sfx: number, music: number): void {
    this.sfxVolume = sfx;
    this.musicVolume = music;
    localStorage.setItem("revival.vol.sfx", String(sfx));
    localStorage.setItem("revival.vol.music", String(music));
    if (this.music) this.music.volume = music;
  }

  /**
   * What the music is doing right now.
   *
   * ⚠️ **EXISTS BECAUSE `new Audio()` IS A DETACHED ELEMENT AND A PROBE CANNOT FIND IT.**
   * `document.querySelectorAll("audio")` returns nothing for it, so a check written that way
   * reports "no music element was ever created" about a controller that is working perfectly. The
   * owner has to be asked.
   */
  musicInfo(): { file: string; paused: boolean; time: number; volume: number;
                 ready: number; error: number | null } | null {
    const m = this.music;
    if (!m) return null;
    return {
      file: m.src.split("/").pop() ?? "",
      paused: m.paused,
      time: Math.round(m.currentTime * 100) / 100,
      volume: Math.round(m.volume * 1000) / 1000,
      ready: m.readyState,
      error: m.error ? m.error.code : null,
    };
  }

  /** True once a gesture (or a prior one) has let audio start. */
  get isArmed(): boolean {
    return this.armed;
  }

  get volumes(): { sfx: number; music: number } {
    return { sfx: this.sfxVolume, music: this.musicVolume };
  }
}

export const audio = new Audio_();
